import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight } from "lucide-react";

import { Chip } from "@/components/people/field";
import { ConflictRow } from "@/components/people/conflict-row";
import { DivergenceResolution } from "@/components/people/divergence-resolution";
import type { PersonDetail } from "@/api/db/people";
import { usePopover } from "@/hooks/use-popover";
import { PessoaLabels } from "@/lib/labels";
import { RevisaoLabels } from "@/lib/labels/revisao";

import type { FieldProblem } from "../-utils/field-problems";

type Conflict = PersonDetail["conflicts"][number];

const LABELS = PessoaLabels.fieldConflict;

function ConflictDetail({ conflict }: { conflict: Conflict }) {
	return (
		<li className="flex flex-col gap-2">
			<div className="flex items-center justify-between gap-2">
				<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					{LABELS.conflicts}
				</span>
				<Chip tone="coral">{RevisaoLabels.resolution[conflict.resolution]}</Chip>
			</div>

			<ConflictRow
				field={conflict.field}
				valueA={conflict.value_a}
				sourceA={conflict.source_a}
				valueB={conflict.value_b}
				sourceB={conflict.source_b}
				detail={conflict.detail === null ? null : RevisaoLabels.detail(conflict.detail)}
			/>

			{!!conflict.related_person && (
				<Link
					to="/pessoa/$id"
					params={{ id: conflict.related_person.id }}
					className="inline-flex w-fit items-center gap-1 rounded-md text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					{LABELS.relatedPerson}: {conflict.related_person.name}
					<ArrowRight className="size-3" />
				</Link>
			)}
		</li>
	);
}

export function FichaFieldConflict({ problem }: { problem?: FieldProblem }) {
	const { open, setOpen, rootRef, panelRef } = usePopover<HTMLSpanElement>();

	if (!problem) {
		return null;
	}

	if (problem.conflicts.length === 0 && problem.divergences.length === 0) {
		return null;
	}

	return (
		<span ref={rootRef} data-slot="field-conflict" className="relative inline-flex">
			<button
				type="button"
				aria-expanded={open}
				aria-label={LABELS.trigger}
				onClick={() => setOpen((value) => !value)}
				className="inline-flex size-4 items-center justify-center rounded-full text-warning transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				<AlertTriangle className="size-3.5" />
			</button>

			{open && (
				<div ref={panelRef} popover="manual" className="anchored-popover w-72 p-3">
					<ul className="flex flex-col gap-3">
						{problem.divergences.map((divergence) => (
							<li key={`d-${divergence.id}`} className="flex flex-col gap-2">
								<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									{LABELS.divergences}
								</span>

								<DivergenceResolution divergence={divergence} onResolved={() => setOpen(false)} />
							</li>
						))}

						{problem.conflicts.map((conflict) => (
							<ConflictDetail
								key={`c-${conflict.field}-${conflict.value_a}-${conflict.value_b}`}
								conflict={conflict}
							/>
						))}
					</ul>
				</div>
			)}
		</span>
	);
}
